import { Link, useParams, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Motion";
import { CtaBand } from "../components/CtaBand";
import { useLang } from "../i18n/LanguageContext";
import { SITE_URL } from "../data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, data } = useLang();
  const sd = t.sd;
  const service = data.SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: service.title,
        description: service.answer,
        url: `${SITE_URL}/services/${service.slug}`,
        provider: { "@type": "ProfessionalService", name: "SG Language Services LLC" },
        areaServed: { "@type": "City", name: "Philadelphia" },
        availableLanguage: ["English", "Spanish"]
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
          { "@type": "ListItem", position: 3, name: service.title, item: `${SITE_URL}/services/${service.slug}` }
        ]
      }
    ]
  };

  const titleWords = service.title.split(" ");
  const mid = Math.ceil(titleWords.length / 2);

  return (
    <>
      <Seo
        title={`${service.title} | SG Language Services LLC`}
        description={service.answer}
        path={`/services/${service.slug}`}
        schema={schema}
      />
      <article data-testid={`service-detail-${service.slug}`}>
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 texture-grain">
          <nav className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted2 mb-8" aria-label="Breadcrumb">
            <Link to="/services" className="hover:text-periwinkle-deep transition-colors duration-300">{sd.breadcrumb}</Link>
            <span className="mx-2">/</span>
            <span className="text-periwinkle-deep">{service.num}</span>
          </nav>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.0] tracking-tight max-w-4xl">
            <MaskedLines lines={[titleWords.slice(0, mid).join(" "), titleWords.slice(mid).join(" ")]} />
          </h1>
          <p className="mt-8 text-lg text-ink/80 leading-relaxed max-w-3xl font-medium">{service.answer}</p>
        </section>

        <section className="bg-white border-y border-ink/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-7">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold">{sd.whatIsPrefix}{service.title.toLowerCase()}{sd.whatIsSuffix}</h2>
              <p className="mt-6 text-muted2 leading-relaxed">{service.body}</p>
              <h3 className="font-semibold text-lg mt-10 mb-4">{sd.whoFor}</h3>
              <ul className="space-y-3">
                {service.useCases.map((u) => (
                  <li key={u} className="flex items-start gap-3 text-sm text-muted2">
                    <Check size={16} className="text-sage-deep mt-0.5 shrink-0" />
                    {u}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border-8 border-cream shadow-xl">
                <img src={service.image} alt={service.title} className="w-full h-[24rem] object-cover" />
              </div>
              <Link to="/contact" data-testid="service-detail-quote-btn" className="block mt-8 rounded-full bg-ink text-cream px-8 py-4 font-semibold text-center hover:bg-periwinkle-deep transition-colors duration-300">
                {sd.quoteFor} {service.title}
              </Link>
            </Reveal>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{sd.processKicker}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold">{sd.processTitle}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {service.process.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08} className="bg-cream p-8">
                <p className="font-mono text-sm text-periwinkle-deep">0{i + 1}</p>
                <h3 className="font-semibold mt-4">{p.step}</h3>
                <p className="text-sm text-muted2 leading-relaxed mt-2">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold mb-8">{sd.faqTitle}</h2>
            <Accordion type="single" collapsible className="border-t border-ink/10">
              {service.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`service-faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold hover:text-periwinkle-deep">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted2 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>
      </article>
      <CtaBand />
    </>
  );
}
